<script>
	import { onMount } from 'svelte';
	import { superAdminAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';

	let { orgId, onClose } = $props();

	let org = $state(null);
	let loading = $state(true);
	let saving = $state(false);
	let activeTab = $state('plan'); // plan | features | limits | users | usage
	let packages = $state([]);

	const FEATURE_DESCRIPTIONS = {
		ai_agents:    { label: 'AI Agents',           desc: 'Agentes IA con tools y auto-aprendizaje' },
		salesbot:     { label: 'Salesbot (flujos)',   desc: 'Builder visual de flujos automatizados' },
		embeddings:   { label: 'Embeddings RAG',       desc: 'Búsqueda semántica en base de conocimiento' },
		send_email:   { label: 'Envío de email',       desc: 'Tool send_email funcional vía SMTP' },
		multi_channel:{ label: 'Multi-canal',          desc: 'WhatsApp + Web + más canales simultáneos' },
		custom_widget:{ label: 'Widget personalizable',desc: 'Tema, colores y branding del web widget' },
		audit_export: { label: 'Exportar auditoría',   desc: 'Descargar audit log de la organización' },
		white_label:  { label: 'White label',          desc: 'Quitar marca "Powered by Mangu"' }
	};

	const LIMIT_FIELDS = [
		{ key: 'max_agents',                  label: 'Usuarios humanos',          unit: 'usuarios' },
		{ key: 'max_channels',                label: 'Canales',                   unit: 'canales' },
		{ key: 'max_ai_agents',               label: 'AI Agents',                 unit: 'agentes' },
		{ key: 'max_conversations_per_month', label: 'Conversaciones/mes',        unit: 'conv/mes' },
		{ key: 'max_knowledge_entries',       label: 'Entradas KB',               unit: 'entradas' },
		{ key: 'max_flows',                   label: 'Flujos Salesbot',           unit: 'flujos' }
	];

	async function load() {
		loading = true;
		try {
			org = await superAdminAPI.getOrg(orgId);
			packages = await superAdminAPI.listPackages();
		} catch (e) { toast.error(e.message); onClose(); }
		finally { loading = false; }
	}

	async function assignPackage(pkg) {
		if (!confirm(`Asignar el paquete "${pkg.name}" a ${org.name}?\nEsto sobrescribirá los módulos y límites actuales.`)) return;
		try {
			const updated = await superAdminAPI.assignPackage(org.id, pkg.id);
			Object.assign(org, updated);
			toast.success(`Paquete ${pkg.name} asignado`);
		} catch (e) { toast.error(e.message); }
	}

	async function save() {
		saving = true;
		try {
			const updated = await superAdminAPI.updateOrg(org.id, {
				name: org.name,
				logo_url: org.logo_url,
				domain: org.domain,
				plan: org.plan,
				max_agents: org.max_agents,
				max_channels: org.max_channels,
				max_ai_agents: org.max_ai_agents,
				max_conversations_per_month: org.max_conversations_per_month,
				max_knowledge_entries: org.max_knowledge_entries,
				max_flows: org.max_flows,
				feature_flags: org.feature_flags
			});
			Object.assign(org, updated);
			toast.success('Organización actualizada');
		} catch (e) { toast.error(e.message); }
		finally { saving = false; }
	}

	async function impersonate(user = null) {
		if (!confirm(`Iniciar sesión como ${user ? user.name : 'admin'} de "${org.name}"?\n(Tu sesión actual será reemplazada por 30 minutos)`)) return;
		try {
			const res = await superAdminAPI.impersonate(org.id, user?.id);
			localStorage.setItem('access_token', res.access_token);
			localStorage.setItem('user', JSON.stringify(res.user));
			localStorage.setItem('impersonating', JSON.stringify({
				org_name: org.name, expires_at: Date.now() + (res.expires_in * 1000)
			}));
			toast.info('Impersonando — recargando...');
			setTimeout(() => window.location.reload(), 800);
		} catch (e) { toast.error(e.message); }
	}

	function pct(used, limit) {
		if (!limit || limit < 0) return 0;
		return Math.min(100, Math.round((used / limit) * 100));
	}

	function fmtDate(iso) {
		if (!iso) return '—';
		try { return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }); }
		catch { return iso; }
	}

	onMount(load);
</script>

<div class="detail">
	<header class="d-head">
		<button class="back" onclick={onClose}><Icon name="arrow-left" size={16} /></button>
		{#if org}
			<div class="d-title">
				<div class="org-mark big">{org.name?.charAt(0)}</div>
				<div>
					<h2>{org.name}</h2>
					<div class="d-meta">
						<span class="badge {org.is_suspended ? 'badge-danger' : 'badge-success'}">
							{org.is_suspended ? 'Suspendida' : 'Activa'}
						</span>
						<span class="muted-sm">{org.slug}</span>
						<span class="muted-sm">· Creada {fmtDate(org.created_at)}</span>
					</div>
				</div>
			</div>
			<div class="d-actions">
				<button class="btn btn-secondary btn-sm" onclick={() => impersonate()}>
					<Icon name="user" size={13} /> Iniciar sesión como
				</button>
				<button class="btn btn-primary btn-sm" onclick={save} disabled={saving}>
					<Icon name="check" size={14} /> {saving ? 'Guardando...' : 'Guardar cambios'}
				</button>
			</div>
		{/if}
	</header>

	{#if loading}
		<div class="loading"><div class="spinner"></div></div>
	{:else if org}
		<div class="d-body">
			<aside class="d-tabs">
				<button class:active={activeTab === 'plan'} onclick={() => (activeTab = 'plan')}>
					<Icon name="star" size={13} /> Plan
				</button>
				<button class:active={activeTab === 'features'} onclick={() => (activeTab = 'features')}>
					<Icon name="zap" size={13} /> Funciones
				</button>
				<button class:active={activeTab === 'limits'} onclick={() => (activeTab = 'limits')}>
					<Icon name="shield" size={13} /> Límites
				</button>
				<button class:active={activeTab === 'usage'} onclick={() => (activeTab = 'usage')}>
					<Icon name="trending-up" size={13} /> Uso actual
				</button>
				<button class:active={activeTab === 'users'} onclick={() => (activeTab = 'users')}>
					<Icon name="users" size={13} /> Usuarios ({org.users?.length || 0})
				</button>
			</aside>

			<main class="d-main">
				{#if activeTab === 'plan'}
					<div class="section">
						<h3>Información general</h3>
						<div class="form-row">
							<label>Nombre
								<input type="text" bind:value={org.name} />
							</label>
							<label>Plan actual
								<select bind:value={org.plan}>
									{#each packages as p (p.id)}
										<option value={p.key}>{p.name}</option>
									{/each}
									{#if org.plan && !packages.some(p => p.key === org.plan)}
										<option value={org.plan}>{org.plan}</option>
									{/if}
								</select>
							</label>
						</div>
						<div class="form-row">
							<label>Dominio personalizado
								<input type="text" bind:value={org.domain} placeholder="app.empresa.com" />
							</label>
							<label>Logo URL
								<input type="text" bind:value={org.logo_url} placeholder="https://..." />
							</label>
						</div>
					</div>

					<div class="section">
						<h3>Paquete asignado</h3>
						<p class="muted">
							{#if org.package_id}
								Esta organización usa el paquete <b>{org.package_name}</b>. Asignar otro sobrescribe sus módulos y límites.
							{:else}
								Sin paquete asignado — asigna uno para aplicar sus módulos y límites.
							{/if}
						</p>
						<div class="preset-row">
							{#each packages.filter(p => p.is_active) as pkg (pkg.id)}
								<button class="preset-btn {pkg.id === org.package_id ? 'current' : ''}" onclick={() => assignPackage(pkg)}>
									<span class="preset-name">{pkg.name}</span>
									<span class="preset-price">${pkg.price_usd ?? 0}/mes{#if pkg.price_crc} · ₡{pkg.price_crc.toLocaleString('es-CR')}{/if}</span>
									<span class="preset-cta">{pkg.id === org.package_id ? 'Actual' : 'Asignar →'}</span>
								</button>
							{/each}
						</div>
						{#if !packages.filter(p => p.is_active).length}
							<p class="muted">No hay paquetes activos. Créalos en la pestaña “Paquetes” del panel.</p>
						{/if}
					</div>
				{/if}

				{#if activeTab === 'features'}
					<div class="section">
						<h3>Feature flags</h3>
						<p class="muted">Activa o desactiva funciones específicas para esta organización. Los usuarios verán las funciones bloqueadas con mensaje de upgrade.</p>
						<div class="features-list">
							{#each Object.entries(FEATURE_DESCRIPTIONS) as [key, info]}
								<label class="feat-row">
									<input
										type="checkbox"
										checked={org.feature_flags?.[key] ?? false}
										onchange={(e) => {
											org.feature_flags = { ...(org.feature_flags || {}), [key]: e.target.checked };
										}}
									/>
									<div class="feat-text">
										<div class="feat-label">{info.label}</div>
										<div class="feat-desc">{info.desc}</div>
									</div>
									<code class="feat-key">{key}</code>
								</label>
							{/each}
						</div>
					</div>
				{/if}

				{#if activeTab === 'limits'}
					<div class="section">
						<h3>Límites del plan</h3>
						<p class="muted">Cuotas máximas. Cuando un usuario intente crear más allá del límite recibirá un error 402 con mensaje de upgrade.</p>
						<div class="limits-grid">
							{#each LIMIT_FIELDS as f}
								<label>
									{f.label}
									<input type="number" min="-1" bind:value={org[f.key]} />
									<small>{f.unit} (-1 = ilimitado)</small>
								</label>
							{/each}
						</div>
					</div>
				{/if}

				{#if activeTab === 'usage'}
					<div class="section">
						<h3>Uso actual vs límites</h3>
						{#if org.usage}
							<div class="usage-grid">
								{#each Object.entries(org.usage) as [key, u]}
									{@const p = pct(u.used, u.limit)}
									<div class="usage-card card">
										<div class="usage-head">
											<span class="usage-label">{key.replace('_', ' ')}</span>
											<span class="usage-num">{u.used.toLocaleString()} / {u.limit < 0 ? '∞' : u.limit.toLocaleString()}</span>
										</div>
										<div class="usage-bar">
											<div class="usage-fill" class:warn={p >= 70 && p < 100} class:full={p >= 100} style="width:{p}%"></div>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					</div>
				{/if}

				{#if activeTab === 'users'}
					<div class="section">
						<h3>Usuarios de la organización</h3>
						<table class="data-table">
							<thead>
								<tr><th>Usuario</th><th>Email</th><th>Rol</th><th>Estado</th><th></th></tr>
							</thead>
							<tbody>
								{#each org.users as u}
									<tr>
										<td>
											<div class="user-cell">
												<div class="org-mark sm" style="background:linear-gradient(135deg, var(--accent), var(--accent-hover))">{u.name?.charAt(0)}</div>
												{u.name}
											</div>
										</td>
										<td class="muted">{u.email}</td>
										<td><span class="badge">{u.role}</span></td>
										<td>{u.is_active ? '✅' : '❌'}</td>
										<td><button class="btn btn-ghost btn-sm" onclick={() => impersonate(u)}>
											<Icon name="user" size={12} /> Login as
										</button></td>
									</tr>
								{/each}
							</tbody>
						</table>
						{#if !org.users?.length}<div class="empty">Sin usuarios</div>{/if}
					</div>
				{/if}
			</main>
		</div>
	{/if}
</div>

<style>
	.detail { position: fixed; inset: 0; background: var(--surface-1); z-index: 50; display: flex; flex-direction: column; }
	.d-head {
		display: flex; align-items: center; gap: 14px;
		padding: 14px 20px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
	}
	.back { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: var(--text-2); }
	.back:hover { background: var(--surface-2); }
	.d-title { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
	.org-mark {
		width: 32px; height: 32px;
		border-radius: 8px;
		background: linear-gradient(135deg, var(--brand), var(--brand-dark));
		color: #fff; font-weight: 700;
		display: flex; align-items: center; justify-content: center;
	}
	.org-mark.big { width: 44px; height: 44px; font-size: 18px; border-radius: 10px; }
	.org-mark.sm { width: 26px; height: 26px; font-size: 11px; }
	.d-title h2 { font-size: 18px; }
	.d-meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
	.muted-sm { color: var(--text-3); font-size: 12px; }
	.d-actions { display: flex; gap: 8px; }

	.loading { flex: 1; display: flex; align-items: center; justify-content: center; }
	.spinner { width: 32px; height: 32px; border: 3px solid var(--border-1); border-top-color: var(--accent); border-radius: 50%; animation: spin 700ms linear infinite; }
	@keyframes spin { to { transform: rotate(360deg); } }

	.d-body { flex: 1; display: grid; grid-template-columns: 200px 1fr; min-height: 0; }
	.d-tabs {
		background: var(--surface-0);
		border-right: 1px solid var(--border-1);
		padding: 14px 10px;
		display: flex; flex-direction: column; gap: 2px;
	}
	.d-tabs button {
		display: flex; align-items: center; gap: 8px;
		padding: 9px 12px;
		font-size: 13px;
		color: var(--text-2);
		border-radius: 8px;
		text-align: left;
		transition: background var(--transition-fast);
	}
	.d-tabs button:hover { background: var(--surface-1); color: var(--text-1); }
	.d-tabs button.active { background: var(--accent-soft); color: var(--accent); font-weight: 600; }

	.d-main { overflow-y: auto; padding: 24px 32px; }
	.section { max-width: 720px; margin-bottom: 28px; }
	.section h3 { font-size: 15px; margin-bottom: 6px; }
	.muted { color: var(--text-3); font-size: 13px; line-height: 1.5; margin-bottom: 14px; }

	.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
	.form-row label { font-size: 12.5px; }

	.preset-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
	.preset-btn {
		display: flex; flex-direction: column; gap: 6px;
		padding: 14px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		text-align: left;
		transition: all var(--transition-fast);
	}
	.preset-btn:hover { border-color: var(--accent); transform: translateY(-2px); }
	.preset-btn.current { border-color: var(--accent); background: var(--accent-soft); }
	.preset-name { font-size: 14px; font-weight: 700; text-transform: capitalize; color: var(--text-1); }
	.preset-price { font-size: 12px; color: var(--text-2); }
	.preset-cta { font-size: 11.5px; color: var(--accent); font-weight: 600; }

	.features-list { display: flex; flex-direction: column; gap: 6px; }
	.feat-row {
		display: grid !important;
		grid-template-columns: auto 1fr auto;
		gap: 12px;
		align-items: center;
		padding: 12px 14px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 8px;
		cursor: pointer;
	}
	.feat-row:hover { border-color: var(--border-2); }
	.feat-row input { width: auto !important; margin: 0 !important; }
	.feat-text { min-width: 0; }
	.feat-label { font-size: 13.5px; font-weight: 600; color: var(--text-1); }
	.feat-desc { font-size: 11.5px; color: var(--text-3); margin-top: 2px; }
	.feat-key {
		font-family: var(--font-mono); font-size: 10.5px;
		background: var(--surface-2); padding: 2px 6px; border-radius: 4px;
		color: var(--text-3);
	}

	.limits-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
	.limits-grid label { font-size: 12.5px; }
	.limits-grid small { display: block; font-size: 11px; color: var(--text-3); margin-top: 4px; }

	.usage-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
	.usage-card { padding: 14px; }
	.usage-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
	.usage-label { font-size: 12.5px; font-weight: 600; color: var(--text-2); text-transform: capitalize; }
	.usage-num { font-size: 13px; font-weight: 700; color: var(--text-1); }
	.usage-bar { height: 6px; background: var(--surface-2); border-radius: 3px; overflow: hidden; }
	.usage-fill { height: 100%; background: var(--success); transition: width 400ms cubic-bezier(0.4, 0, 0.2, 1); }
	.usage-fill.warn { background: var(--warning); }
	.usage-fill.full { background: var(--danger); }

	.data-table { width: 100%; border-collapse: collapse; background: var(--surface-0); border-radius: 10px; overflow: hidden; border: 1px solid var(--border-1); font-size: 13px; }
	.data-table th { background: var(--surface-1); text-align: left; padding: 10px 14px; font-size: 11.5px; font-weight: 700; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid var(--border-1); }
	.data-table td { padding: 12px 14px; border-bottom: 1px solid var(--border-1); }
	.data-table tr:last-child td { border-bottom: none; }
	.user-cell { display: flex; align-items: center; gap: 8px; }
	.empty { text-align: center; padding: 40px; color: var(--text-3); font-size: 13px; }
</style>
